
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FastForexSettings } from "@/components/integrations/FastForexSettings";
import { CoingeckoSettings } from "@/components/integrations/CoingeckoSettings";
import { TelegramSettings } from "@/components/integrations/TelegramSettings";
import { TwitterSettings } from "@/components/integrations/TwitterSettings";
import { YoutubeSettings } from "@/components/integrations/YoutubeSettings";
import { IntegrationSchedules } from "@/components/integrations/IntegrationSchedules";
import { TwitterSync } from "@/components/integrations/TwitterSync";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { triggerScheduledUpdates } from "@/utils/engagementMetrics";

const apiKeysSchema = z.object({
  fastForexApiKey: z.string().optional(),
  coingeckoApiKey: z.string().optional(),
  telegramApiId: z.string().optional(),
  telegramApiHash: z.string().optional(),
  telegramSession: z.string().optional(),
  twitterApiKey: z.string().optional(),
  twitterApiSecret: z.string().optional(),
  twitterBearerToken: z.string().optional(),
  youtubeApiKey: z.string().optional(),
});

type FormValues = z.infer<typeof apiKeysSchema>;

const IntegrationSettings = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("api-keys");
  const [showTwitterRateLimit, setShowTwitterRateLimit] = useState(false);
  const [runningAllUpdates, setRunningAllUpdates] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(apiKeysSchema),
    defaultValues: {
      fastForexApiKey: "",
      coingeckoApiKey: "",
      telegramApiId: "",
      telegramApiHash: "",
      telegramSession: "",
      twitterApiKey: "",
      twitterApiSecret: "",
      twitterBearerToken: "",
      youtubeApiKey: "",
    },
  });

  useEffect(() => {
    const loadApiKeys = async () => {
      try {
        const { data: fastForexData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'fastforex')
          .maybeSingle();
          
        const { data: coingeckoData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'coingecko')
          .maybeSingle();
          
        const { data: telegramApiIdData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'telegram_api_id')
          .maybeSingle();
          
        const { data: telegramApiHashData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'telegram_api_hash')
          .maybeSingle();

        const { data: telegramSessionData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'telegram_session')
          .maybeSingle();

        const { data: twitterApiKeyData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'twitter_api_key')
          .maybeSingle();
          
        const { data: twitterApiSecretData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'twitter_api_secret')
          .maybeSingle();
          
        const { data: twitterBearerTokenData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'twitter_bearer_token')
          .maybeSingle();

        const { data: youtubeApiKeyData } = await supabase
          .from('integration_settings')
          .select('api_key')
          .eq('integration_name', 'youtube_api_key')
          .maybeSingle();

        form.setValue('fastForexApiKey', fastForexData?.api_key || "");
        form.setValue('coingeckoApiKey', coingeckoData?.api_key || "");
        form.setValue('telegramApiId', telegramApiIdData?.api_key || "");
        form.setValue('telegramApiHash', telegramApiHashData?.api_key || "");
        form.setValue('telegramSession', telegramSessionData?.api_key || "");
        form.setValue('twitterApiKey', twitterApiKeyData?.api_key || "");
        form.setValue('twitterApiSecret', twitterApiSecretData?.api_key || "");
        form.setValue('twitterBearerToken', twitterBearerTokenData?.api_key || "");
        form.setValue('youtubeApiKey', youtubeApiKeyData?.api_key || "");
        
        // Show Twitter rate limit warning if Twitter API keys are set
        setShowTwitterRateLimit(!!(twitterApiKeyData?.api_key || twitterApiSecretData?.api_key || twitterBearerTokenData?.api_key));
      } catch (error) {
        console.error("Error loading API keys:", error);
        toast({
          title: "Error Loading Settings",
          description: "Failed to load existing API keys.",
          variant: "destructive",
        });
      }
    };
    
    loadApiKeys();
  }, [form, toast]);

  const runAllUpdatesNow = async () => {
    try {
      setRunningAllUpdates(true);
      toast({
        title: "Running All Updates",
        description: "Manually running updates for all configured platforms...",
      });
      
      const result = await triggerScheduledUpdates();
      
      toast({
        title: "Updates Complete",
        description: "All platform updates have been processed.",
      });
    } catch (error: any) {
      console.error("Error running all updates:", error);
      toast({
        title: "Update Error",
        description: error.message || "Failed to run scheduled updates.",
        variant: "destructive",
      });
    } finally {
      setRunningAllUpdates(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Integration Settings</h1>
      
      {showTwitterRateLimit && (
        <Alert variant="warning" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Twitter API Rate Limits</AlertTitle>
          <AlertDescription>
            <p>
              Twitter API has strict rate limits that can affect data retrieval. The system is configured to:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Process KOLs in batches of 2 to minimize rate limiting errors</li>
              <li>Apply exponential backoff for retries when rate limits are hit</li>
              <li>Support background processing for large data extraction jobs</li>
            </ul>
            <p className="mt-2">
              For large KOL sets (10+), always use the background processing mode to avoid timeouts.
            </p>
          </AlertDescription>
        </Alert>
      )}
      
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertTitle className="text-blue-700">Enhanced Scheduled Updates</AlertTitle>
        <AlertDescription className="text-blue-600">
          <p className="mb-2">Content metrics now support automatic scheduled updates. You can:</p>
          <ul className="list-disc ml-6 mb-2">
            <li>Set individual update schedules for YouTube, Twitter, and Telegram</li>
            <li>Run manual updates for immediate data refreshes</li>
            <li>See the last time content was refreshed</li>
          </ul>
          <div className="mt-3">
            <Button 
              variant="outline" 
              size="sm"
              className="border-blue-500 text-blue-700 hover:bg-blue-100"
              onClick={runAllUpdatesNow}
              disabled={runningAllUpdates}
            >
              {runningAllUpdates ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Run All Updates Now
            </Button>
          </div>
        </AlertDescription>
      </Alert>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-white dark:bg-transparent border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <TabsTrigger value="api-keys" className="text-gray-800 dark:text-gray-200">API Keys</TabsTrigger>
          <TabsTrigger value="schedules" className="text-gray-800 dark:text-gray-200">Update Schedules</TabsTrigger>
          <TabsTrigger value="twitter-sync" className="text-gray-800 dark:text-gray-200">Twitter Sync</TabsTrigger>
        </TabsList>
        
        <TabsContent value="api-keys" className="space-y-8">
          <FastForexSettings form={form} />
          <CoingeckoSettings form={form} />
          <TelegramSettings form={form} />
          <TwitterSettings form={form} />
          <YoutubeSettings form={form} />
        </TabsContent>
        
        <TabsContent value="schedules" className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <IntegrationSchedules 
              integrationType="youtube"
              title="YouTube"
              description="Schedule automatic updates for YouTube engagement metrics"
            />
            
            <IntegrationSchedules 
              integrationType="twitter"
              title="Twitter"
              description="Schedule automatic updates for Twitter engagement metrics"
            />
            
            <IntegrationSchedules 
              integrationType="telegram"
              title="Telegram"
              description="Schedule automatic updates for Telegram engagement metrics"
            />
          </div>
        </TabsContent>

        <TabsContent value="twitter-sync" className="space-y-8">
          <TwitterSync />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationSettings;
